import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocaleService } from '@app/services/locale/locale.service';
import interpolate from '@app/shared/utils/string/interpolate';
import { AffiliationsService } from '../affiliations/affiliations.service';
import { ConfigService } from '@core/config.service';
import { ExpirableSessionStorageService } from '@app/shared/services/expirable-storage/expirable-session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ResourceBundleService {
  siteId = 'dcl';
  url = '/resource-bundle${watcherSuffix}/${brand}/${locale}/homepage/';
  defaultAffiliation = 'standard';
  defaultProfile = 'looker';
  currentUserInfo;
  useWatcher = false;
  sessionTTL = 60000 * 4; // 4 minutes

  constructor(
    private httpClient: HttpClient,
    private localeService: LocaleService,
    private affiliationService: AffiliationsService,
    private configService: ConfigService,
    private storage: ExpirableSessionStorageService
  ) {
    const toggles = this.configService.getValue('toggles');
    this.useWatcher = toggles.useWatcherForHomepageContent;
    if (toggles.resourceBundleStorageTTL) {
      this.sessionTTL = toggles.resourceBundleStorageTTL;
    }
  }

  get(type: 'stories' | 'heroes'): Promise<any> {
    return this.getCachedContent().then(content => {
      const storyOrderingPreset = content.storyOrderingPresets.presets.A;
      content = this.extractRelevantStoriesData(content, type);
      content = content.sort(function (a, b) {
        const val = parseInt(a.generalPurposeStrings.order, 10) - parseInt(b.generalPurposeStrings.order, 10);
        return val;
      });
      return {[type]: content, storyOrderingPreset};
    });
  }

  private getCachedContent(): Promise<any> {
    const url = this.getURL();
    const cache = this.storage.getItem(url);
    return this.affiliationService.getAffiliations('dcl').then(affiliations => {
      this.extractUserInfo(affiliations);
      if (cache) {
        return Promise.resolve(JSON.parse(cache));
      } else {
        return this.httpClient.get<any>(url).toPromise().then(content => {
          // old homepage didn't have an expire for non-watcher service, but we'll do
          // it for both here since all brands will eventually move to watcher anyway
          const expireTime = new Date().getTime() + this.sessionTTL; // 4 mins in the future
          const expireIsoDate = new Date(expireTime).toISOString();
          this.storage.setItem(url, JSON.stringify(content), expireIsoDate);
          return content;
        });
      }
    });
  }

  private getURL(): string {
    const locale = this.localeService.getLocale() || 'en';
    const brand = 'dcl';
    const watcherSuffix = this.useWatcher ? '-watcher' : '';

    return interpolate(this.url, { brand, locale, watcherSuffix });
  }

  /*
   * Filter content based on the current user's affiliation(s)/profile(s)
   * @param {Object} resource bundle content
   * @param {String} content type to return (heroes|stories)
   * @returns {Array} filtered stories content
   */
  extractRelevantStoriesData(dataBundle, dataSet) {
    const currentUserAffiliations = this.currentUserInfo.affiliations;
    let chosenAffiliation;

    /* If there are more than 1 affiliations for the current user choose higher priority affiliation
      * higher index means higher priority
      */
    if (dataBundle.affiliationPriority &&
        dataBundle.affiliationPriority.length > 1 &&
        currentUserAffiliations.length > 1) {
        let currentPriority = -99;
        for (let j = 0; j < currentUserAffiliations.length; j++) {

            const checkAffil = currentUserAffiliations[j].toLowerCase(),
                checkIndex = dataBundle.affiliationPriority.indexOf(checkAffil);
            if (checkIndex > currentPriority) {
                currentPriority = checkIndex;
                const testAffiliation = dataBundle.affiliationPriority[checkIndex];
                if (dataBundle[dataSet][testAffiliation] &&
                    dataBundle[dataSet][testAffiliation].length > 0) {
                    chosenAffiliation = dataBundle.affiliationPriority[checkIndex];
                }
            }
        }
    } else if (dataBundle.affiliationPriority &&
        dataBundle.affiliationPriority.length === 1) {
        chosenAffiliation = dataBundle.affiliationPriority[0];
    } else {
        chosenAffiliation = this.defaultAffiliation;
    }

    return dataBundle[dataSet][chosenAffiliation];
}

  extractUserInfo(affiliation) {
    // Set default affiliation as fallback
    const affiliationList = [this.defaultAffiliation];
    // Create affiliation array and standardize default affiliation
    if (affiliation !== undefined &&
       affiliation[this.siteId] !== undefined &&
       affiliation[this.siteId].storedAffiliations !== undefined &&
       affiliation[this.siteId].storedAffiliations.length !== 0
    ) {
        for (const key in affiliation[this.siteId].storedAffiliations) {
            if (affiliation[this.siteId].storedAffiliations[key] !== 'STD_GST') {
                affiliationList.push(affiliation[this.siteId].storedAffiliations[key]);
            }
        }
    }

    // TODO: add in dynamic profile function instead of static text
    this.currentUserInfo = {
        affiliations: affiliationList,
        profile: this.defaultProfile
    };

    return this.currentUserInfo;
  }

}
